import { extend } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";
import { join, keys } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useMediaQuery } from "usehooks-ts";
import Highcharts, {
  color,
  // getOptions
} from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HighchartsAccessibility from "highcharts/modules/accessibility";
import mouseWheelZoom from "highcharts/modules/mouse-wheel-zoom";
import { PoolAnalyticsDataSamples } from "@axvdex/views/Statistics";
import { abbreviateUSD } from "@axvdex/utils/formatNumber";
import { selectAssets, selectPools } from "@axvdex/state/wallet/walletSelectors";
import { useAppSelector } from "@axvdex/state";
import { responsiveBreakpoints } from "@axvdex/constants";
import useLanguage from "@axvdex/hooks/useLanguage";

import CustomInputButton from "../form-element/CustomInputButton";
import CustomSelect, { ListItemsProps } from "../form-element/CustomSelect";

HighchartsAccessibility(Highcharts);
extend(customParseFormat);
extend(utc);
mouseWheelZoom(Highcharts);

//const startDate = new Date(1688338800);

const poolsFilterSorter = (a, b) => {
  if ("allPools" === a.value) return -1;
  if ("allPools" === b.value) return 1;

  return ("" + a.label).localeCompare(b.label);
};

const LiquidityChart = ({
  analyticsData,
  curLiquidity,
  topFilter,
}: {
  analyticsData: PoolAnalyticsDataSamples;
  curLiquidity: number;
  topFilter: string;
}) => {
  const { i18 } = useLanguage();

  const TimeFilters = [
    {
      id: "daily",
      label: i18("Daily", "statistics.liquidityChart.daily"),
      mobileLabel: i18("D", "statistics.liquidityChart.mobileLabel.daily"),
    },
    {
      id: "weekly",
      label: i18("Weekly", "statistics.liquidityChart.weekly"),
      mobileLabel: i18("W", "statistics.liquidityChart.mobileLabel.weekly"),
    },

    {
      id: "monthly",
      label: i18("Monthly", "statistics.liquidityChart.monthly"),
      mobileLabel: i18("M", "statistics.liquidityChart.mobileLabel.monthly"),
    },
    // { id: "all", label: i18("All Time", "statistics.liquidityChart.allTime") , mobileLabel: i18("All", "statistics.liquidityChart.mobileLabel.allTime") },
  ];
  const pools = useAppSelector(selectPools);
  const assets = useAppSelector(selectAssets);

  const isMobileBreakpoint = useMediaQuery(responsiveBreakpoints.mobile);

  const [poolOptions, setPoolOptions] = useState<ListItemsProps[]>([]);
  const [poolFilter, setFilter] = useState("allPools");

  const [timeFilter, setTimeFilter] = useState("daily");

  const handleFilterChange = e => {
    setFilter(e.value);
  };

  // every time top filter changes, reset the subfilters
  useEffect(() => {
    setFilter("allPools");
    setTimeFilter("daily");
  }, [topFilter]);

  useEffect(() => {
    if (pools && Object.keys(pools).length > 0) {
      const poolIds = keys(analyticsData);

      const getPoolLabel = pool => {
        const assetIDs = [];
        pool?.poolAssets?.forEach(asset => {
          const assetId = asset.info?.token?.contract_addr || asset.info?.native_token?.denom;
          assetIDs.push(assetId);
        });
        return join(
          assetIDs.map(assetID => assets[assetID]?.symbol),
          " "
        );
      };

      const options = poolIds
        .filter(poolId => "allPools" === poolId || ("allPools" !== poolId && pools[poolId])) // added to prevent showing pools that are disabled for this contract env
        .map(poolId => {
          if ("allPools" === poolId) {
            return { label: i18("All Pools", "statistics.liquidityChart.allPools"), value: "allPools" };
          }
          return { label: getPoolLabel(pools[poolId]), value: poolId };
        })
        .sort(poolsFilterSorter);

      setPoolOptions(options);
    }
  }, [analyticsData, pools]);

  const liquidityChartData = useMemo(() => {
    let sortedData = analyticsData[poolFilter];

    if ("weekly" === timeFilter) {
      // Create an object to store weekly sums
      const weeklySums = {};
      const weeklyCounts = {};

      // Iterate through the data and group by week
      sortedData.forEach(entry => {
        // Calculate the start of the week for the current timestamp
        const startOfWeek = new Date(entry[0]);
        const dayOfWeek = startOfWeek.getDay();
        const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        startOfWeek.setDate(startOfWeek.getDate() - daysToSubtract);
        const startOfWeekTimestamp = "" + startOfWeek.getTime();

        // If the week is not in the object, create a new entry
        if (!weeklySums[startOfWeekTimestamp]) {
          weeklySums[startOfWeekTimestamp] = [0, 0];
          weeklyCounts[startOfWeekTimestamp] = 0;
        }

        // Add the value to the corresponding week
        weeklySums[startOfWeekTimestamp][0] += entry[1];
        weeklySums[startOfWeekTimestamp][1] += entry[2];
        weeklyCounts[startOfWeekTimestamp] += 1;
      });

      sortedData = Object.keys(weeklySums).map(timestamp => {
        return [parseFloat(timestamp), weeklySums[timestamp][0] / weeklyCounts[timestamp], weeklySums[timestamp][1]];
      });
    }

    if ("monthly" === timeFilter) {
      // Create an object to store weekly sums
      const monthlySums = {};
      const monthlyCounts = {};

      // Iterate through the data and group by week
      sortedData.forEach(entry => {
        const year = new Date(entry[0]).getFullYear();
        const month = new Date(entry[0]).getMonth();
        const startOfMonth = new Date(year, month, 1).getTime();

        // If the week is not in the object, create a new entry
        if (!monthlySums[startOfMonth]) {
          monthlySums[startOfMonth] = [0, 0];
          monthlyCounts[startOfMonth] = 0;
        }

        // Add the value to the corresponding week
        monthlySums[startOfMonth][0] += entry[1];
        monthlySums[startOfMonth][1] += entry[2];
        monthlyCounts[startOfMonth] += 1;
      });

      sortedData = Object.keys(monthlySums).map(timestamp => {
        return [parseFloat(timestamp), monthlySums[timestamp][0] / monthlyCounts[timestamp], monthlySums[timestamp][1]];
      });
    }

    // console.log(sortedData);
    return sortedData;
  }, [poolOptions, poolFilter, timeFilter]);

  const disableFilter = label => {
    const startDate = new Date(1688338800000);
    if ("Daily" === label) return false;
    if ("Weekly" === label) {
      startDate.setDate(startDate.getDate() + 7);
      return new Date() < startDate;
    }
    if ("Monthly" === label) {
      startDate.setDate(startDate.getDate() + 30);
      return new Date() < startDate;
    }
  };

  return (
    <div className="statsSectionGridItem">
      <div className="statsSectionGraphGridItemHeader">
        <div className="statsSectionGraphGridItemHeaderText">
          <p className="statsSectionGridItemTitle textGrey">
            {i18("Current Liquidity", "statistics.liquidityChart.Title")}
          </p>

          <span className="statsSectionGridItemValue pricing">
            {curLiquidity && "allPools" === poolFilter
              ? abbreviateUSD(curLiquidity)
              : liquidityChartData && liquidityChartData.length > 0
              ? abbreviateUSD(liquidityChartData[liquidityChartData.length - 1][1])
              : "-"}
          </span>

          {/*<p className="statsSectionGridItemTitle textGrey">{t("Average of last 24h")}</p>*/}
        </div>

        <div className="inlineFlexbox">
          <CustomSelect
            name="liquidity_pools"
            labelText={i18("Filter by pools", "statistics.liquidityChart.labelText")}
            hiddenLabel={true}
            value={poolOptions[poolOptions.findIndex(option => option.value === poolFilter)]}
            items={poolOptions}
            // menuIsOpen={true}
            onChange={handleFilterChange}
          />
        </div>
      </div>

      <div className="btnGroup statsSectionGraphBtnGroup">
        {TimeFilters.map((item, idx) => (
          <CustomInputButton
            title="sdfsa"
            disabled={disableFilter(item.label)}
            key={idx}
            type="radio"
            id={item.id}
            name="liquidity_time_filter"
            labelText={isMobileBreakpoint ? item.mobileLabel : item.label}
            checked={timeFilter === item.id}
            onChange={() => setTimeFilter(item.id)}
          />
        ))}
      </div>

      <div className="statsGraphWrapper liquidity" key={poolFilter + "_" + timeFilter + "_" + poolOptions}>
        <HighchartsReact
          highcharts={Highcharts}
          options={{
            chart: {
              panning: true,
              zooming: {
                mouseWheel: {
                  type: "x",
                },
              },
              height: "250rem",
              backgroundColor: "",
              events: {
                load: chartObject => {
                  if (liquidityChartData && liquidityChartData.length > 0) {
                    const min =
                      liquidityChartData[liquidityChartData.length > 50 ? liquidityChartData.length - 50 : 0][0];
                    const max = liquidityChartData[liquidityChartData.length - 1][0];
                    const maxValue = liquidityChartData.reduce((a, b) => Math.max(a, b[1]), 0);

                    chartObject.target.xAxis[0].setExtremes(min, max);
                    chartObject.target.yAxis[0].setExtremes(0, maxValue + maxValue * 0.05);
                  }
                },
              },
            },
            title: {
              text: "",
            },
            accessibility: {
              enabled: true,
              description: "Liquidity",
            },
            xAxis: {
              type: "datetime",
              maxRange: 30 * 24 * 3600 * 1000,
              labels: {
                style: { color: "#666" },
              },
            },
            yAxis: {
              title: {
                text: "",
              },
              gridLineWidth: 0,
              opposite: true,
              labels: {
                formatter: sample => {
                  return abbreviateUSD(sample.value);
                },
                style: { color: "#666" },
              },
              //tickInterval: 100000,
              tickAmount: 8,
            },
            legend: {
              enabled: false,
            },
            tooltip: {
              formatter() {
                const options = {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                };
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                const sampleDate = new Date(this.x).toLocaleDateString("en-uk", options);
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                return `${sampleDate}<br/><b>${abbreviateUSD(this.y)}</b>`;
              },
            },
            plotOptions: {
              area: {
                enableMouseTracking: true,
                color: color("#37c739").setOpacity(0.8).get("rgba"),
                fillColor: {
                  linearGradient: {
                    x1: 1,
                    y1: 1,
                    x2: 0,
                    y2: 0,
                  },
                  stops: [
                    [0, color("#00e272").setOpacity(0).get("rgba")],
                    [1, "#00e272"],
                    // [0, getOptions().colors[0]],
                    // [1, color(getOptions().colors[0]).setOpacity(0).get("rgba")],
                  ],
                },
                marker: {
                  radius: 2,
                },
                lineWidth: 1,
                states: {
                  hover: {
                    lineWidth: 1,
                  },
                },
                threshold: null,
              },
            },

            series: [
              {
                type: "area",
                name: "",
                data: liquidityChartData.map(sample => [sample[0], sample[1]]),
              },
            ],
            credits: {
              enabled: false,
            },
          }}
        />
      </div>
    </div>
  );
};

export default LiquidityChart;
